import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import Logo from '../components/Logo';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attemptNum = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    
    if (attemptNum >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your membership status in the dashboard.');
      return;
    }

    try {
      // Use the new Stripe checkout status endpoint
      // Ensure we have auth token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found, redirecting to login...');
        setStatus('error');
        setMessage('Session expired. Please log in again to verify your payment.');
        return;
      }

      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        // Confirm payment complete and process discounts/referrals
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API}/subscriptions/confirm-payment-complete?session_id=${sessionId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (confirmError) {
          console.log('Discount confirmation:', confirmError.response?.data || confirmError.message);
          // Don't fail the success flow if this fails
        }
        
        setStatus('success');
        setMessage(response.data.message || 'Payment successful! Your membership has been activated.');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Still processing (open/unpaid), continue polling
        setAttempts(attemptNum + 1);
        setMessage(`Verifying your payment... (${attemptNum + 1}/${maxAttempts})`);
        setTimeout(() => checkPaymentStatus(sessionId, attemptNum + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Error detail:', errorDetail);
      
      // If it's a 401/403, session might have expired
      if (error.response?.status === 401 || error.response?.status === 403) {
        setStatus('error');
        setMessage('Session expired. Please log in again to verify your payment.');
        return;
      }
      
      // If transaction not found, maybe user paid a different session
      if (error.response?.status === 404) {
        setStatus('error');
        setMessage('Payment session not found. Please check your dashboard or contact support.');
        return;
      }
      
      // Retry a few times before giving up for network errors
      if (attemptNum < maxAttempts - 1) {
        setAttempts(attemptNum + 1);
        setTimeout(() => checkPaymentStatus(sessionId, attemptNum + 1), pollInterval);
      } else {
        setStatus('error');
        setMessage(`Error verifying payment: ${errorDetail}. Please check your dashboard or contact support.`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo className="h-16 w-16" />
          </div>
          <CardTitle className="text-2xl">Payment Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 text-center">
          {status === 'checking' && (
            <div className="space-y-4">
              <Loader2 className="h-16 w-16 text-teal-600 animate-spin mx-auto" />
              <p className="text-gray-600">{message}</p>
              <p className="text-sm text-gray-500">Attempt {attempts + 1} of 5...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Payment Successful!</h3>
                <p className="text-gray-600">{message}</p>
              </div>
              <Button
                onClick={() => navigate('/patient?refresh=true')}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="goto-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <span className="text-3xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Payment Issue</h3>
                <p className="text-gray-600">{message}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/patient')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Dashboard
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
